import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMessages } from '../contexts/MessageContext';
import { useAuth } from '../auth/AuthContext';

const DashboardCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow p-4">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <div>{children}</div>
  </div>
);

function ContractionTimerCard() {
  const [isTiming, setIsTiming] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState(0);
  const [history, setHistory] = useState([]);
  const [lastEndTime, setLastEndTime] = useState(null);

  useEffect(() => {
    let interval;
    if (isTiming) {
      interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isTiming, startTime]);

  const format = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec < 10 ? "0" : ""}${sec}`;
  };

  const handleToggle = () => {
    if (isTiming) {
      const endTime = Date.now();
      const newEntry = {
        start: new Date(startTime),
        duration,
        interval: lastEndTime ? Math.floor((startTime - lastEndTime) / 1000) : null,
      };
      setHistory([newEntry, ...history]);
      setLastEndTime(endTime);
      setIsTiming(false);
      setStartTime(null);
      setDuration(0);
    } else {
      setStartTime(Date.now());
      setIsTiming(true);
    }
  };

  return (
    <DashboardCard title="Contraction Timer">
      <div className="space-y-2">
        <p className="text-lg">{isTiming ? `Timing: ${format(duration)}` : "Not timing"}</p>
        <button
          onClick={handleToggle}
          className={`w-full py-2 px-4 rounded text-white font-semibold ${
            isTiming ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {isTiming ? "Stop Contraction" : "Start Contraction"}
        </button>
        {history.length > 0 && (
          <div>
            <h3 className="font-semibold mt-4 mb-2 text-sm text-gray-700">History</h3>
            <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
              {history.map((entry, idx) => (
                <li key={idx} className="border-b pb-1">
                  {format(entry.duration)} duration
                  {entry.interval !== null && (
                    <span className="text-gray-500"> — {format(entry.interval)} since last</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardCard>
  );
}

export default function ClientDashboard() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const { messages } = useMessages();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('velaToken');
    logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 text-gray-900 font-sans">
      {/* Mobile Menu Button */}
      <div className="md:hidden p-4 bg-emerald-600 text-white flex justify-between items-center">
        <h1 className="text-2xl font-bold">Vela</h1>
        <button
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="text-white focus:outline-none"
        >
          ☰
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-emerald-600 text-white p-6 transform md:relative md:translate-x-0 transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:block`}
      >
        <div className="flex flex-col h-full">
          <h1 className="text-3xl font-bold mb-8 tracking-tight">Vela</h1>
          <nav className="flex flex-col gap-3 text-lg flex-grow">
            <Link to="/client" className="hover:text-emerald-200">Dashboard</Link>
            <Link to="/client/messages" className="hover:text-emerald-200">Messages</Link>
            <Link to="/client/appointments" className="hover:text-emerald-200">Appointments</Link>
            <Link to="/client/documents" className="hover:text-emerald-200">Documents</Link>
            <Link to="/client/midwife" className="hover:text-emerald-200">My Midwife</Link>
            <Link to="/client/settings" className="hover:text-emerald-200">Settings</Link>
          </nav>
          
          <button
            onClick={handleLogout}
            className="mt-8 py-2 px-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded transition-colors duration-200 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h5v-2H4V5h4V3H3zm12.293 2.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M9 3a1 1 0 000 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L13 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Dashboard Content */}
      <main className="flex-1 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <DashboardCard title="Next Appointment">
          <p>May 15 – 11:00 AM at Main Birth Center</p>
        </DashboardCard>

        <DashboardCard title="Messages">
          {messages
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .slice(0, 3)
            .map((message, index) => (
              <div key={message.id || index} className="p-2 border-b last:border-b-0">
                <div className="flex justify-between items-start">
                  <p className="text-sm text-gray-800 break-words">{message.content}</p>
                  <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                    {new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(message.timestamp).toLocaleDateString()}
                </p>
              </div>
            ))}
          <Link 
            to="/client/messages" 
            className="block mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
          >
            View all messages →
          </Link>
        </DashboardCard>

        <DashboardCard title="Shared Documents">
          <ul className="list-disc list-inside text-sm">
            <li>Consent Form.pdf</li>
            <li>Birth Plan.pdf</li>
          </ul>
        </DashboardCard>

        <DashboardCard title="To-Do Items">
          <ul className="list-disc list-inside text-sm">
            <li>Complete intake questionnaire</li>
            <li>Sign HIPAA consent form</li>
          </ul>
        </DashboardCard>

        <DashboardCard title="My Midwife">
          <p className="font-semibold">Sarah Johnson, CPM</p>
          <p className="text-sm">Phone: (555) 123-4567</p>
          <button className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded">
            Message Midwife
          </button>
        </DashboardCard>

        <DashboardCard title="Birth Preferences">
          <p className="text-sm">Home birth, water preferred, delayed cord clamping</p>
        </DashboardCard>

        <DashboardCard title="Lab Results">
          <ul className="text-sm">
            <li>Iron: Normal</li>
            <li>Vitamin D: Low</li>
          </ul>
        </DashboardCard>

        <ContractionTimerCard />
      </main>
    </div>
  );
}
